import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Seo,
  Anteroom,
  PlayerLanding,
  ErrorCard,
  Loader,
  Input,
  Spinner,
  Flixx
} from 'ui'
import {
  useIsPurchased,
  useSession,
  useApi,
  useModal
} from 'hooks'
import {
  useBuyContent,
  useGetContent,
  useGetProfileMinimal
} from 'api/content'
import { useGetBalance } from 'api/user'
import {
  useGetAd,
  useGetNextReward,
  usePostWatchedAd
} from 'api/ads'

import { getThumbnail } from 'utils/thumbnails'
import { formatAmount } from 'utils/amounts'
import { formatDuration } from 'utils/duration'
import { GAHandler } from 'utils/GA'

import { SerieInfo } from './SerieInfo'
import { EpisodeInfo } from './EpisodeInfo'
import EpisodeCard from './EpisodeCard'

import { environment } from 'config/environment'

import { Episodes, Season } from './styles'


const NewLoader = ({ text }) => (
  <div>
    <Spinner size={48} />
    <p style={{ marginTop: 16 }}>{text}</p>
  </div>
)


const AnteroomSerie = () => {

  const {
    serieUUID,
    customSeason,
    customEpisode
  } = useParams()

  const { t } = useTranslation()
  const history = useHistory()
  const { Modal } = useModal('welcome')
  const { apiFetch } = useApi()

  const { isLoggedIn } = useSession()
  const { data: balance } = useGetBalance()
  const { data: serie, error: serieError } = useGetContent({ uuid: serieUUID, contentType: 'serie' })
  const { data: author } = useGetProfileMinimal({ userID: serie?.authorId })
  const { isPurchased, isPurchasesLoaded } = useIsPurchased({ contentType: 'series' })

  const {
    data: ad,
    refetch: getAd,
    remove: resetAd
  } = useGetAd()

  const {
    data: nextReward,
    refetch: getNextReward,
    remove: resetNextReward
  } = useGetNextReward()

  const [weblink, setWeblink] = useState('')

  const { buyContent, isLoading: isBuying } = useBuyContent()
  const { postWatchedAd, isLoading: isClaimingReward } = usePostWatchedAd()

  const [seasonSelected, setSeasonSelected] = useState()
  const [showAd, setShowAd] = useState(false)
  const [isCreatorReceivingFlixx, setIsCreatorReceivingFlixx] = useState(false)

  const episodes = seasonSelected?.content || [];

  const getNextEpisode = (season) => {
    if (!season?.content.length) return

    const lastEpPurchasedIdx = [...season.content]
      .findLastIndex(ep => isPurchased(ep.uuid))

    const nextEp = season?.content[lastEpPurchasedIdx + 1]

    return nextEp
  }


  const [episodeSelected, setEpisodeSelected] = useState()
  const [nextEpisode, setNextEpisode] = useState()
  const [epCurrentlyWatching, setEpCurrentlyWatching] = useState()
  const [playContent, setPlayContent] = useState()

  const episodeToWatch = episodeSelected // null when plays without selecting and when auto-buy next episode
    || nextEpisode // null when plays last episode (shoudl this just use for next reproduction purpose?)
    || getNextEpisode(seasonSelected) // null when all season is bought (suggested?) (should move the user to next season? if exists)
    || episodes[0] // default first chapter

  const selected = episodeSelected || serie

  const titlePlayer = serie?.title
  const seasonNumber = seasonSelected?.number
  const episodeNumber = epCurrentlyWatching?.order + 1
  const episodeTitle = epCurrentlyWatching?.title

  const seasonEpisode = t('seasonEpisodeHyphen', {
    season: seasonNumber,
    episode: episodeNumber,
  })

  const descriptionPlayer = `${seasonEpisode}: ${episodeTitle}`

  const isWaiting = isClaimingReward || isBuying

  const handleSelectSeason = ({ value }) => {
    const selected = serie.season.find(season => season.number === value)
    setSeasonSelected(selected)
    setEpisodeSelected(null)
  }

  const handleSelectEpisode = (ep, play ) => {
    if (customEpisode) history.push(`/series/${serieUUID}`)
    setEpisodeSelected(ep)
    

    if (play) setPlayContent(ep)
    else window.scrollTo({ top: 0 })
  }

  const handlePlayContent = () => {
    setEpCurrentlyWatching(episodeToWatch)
    const next = episodes.find(ep => ep.order === episodeToWatch.order + 1)
    setNextEpisode(next)
  }

  const handleBuyContent = () => {
    setIsCreatorReceivingFlixx(true)

    buyContent({
      contentId: episodeToWatch.uuid,
      price: episodeToWatch.price
    })
      .then(handlePlayContent)
  }

  const handleCloseEpisode = ({ selectNext } = {}) => {
    setEpCurrentlyWatching(null)
    setWeblink('')

    let next = epCurrentlyWatching

    next = episodes.find(
      ep => ep.order === epCurrentlyWatching.order + 1
    )
    if (!next) next = null

    setEpisodeSelected(next)
  }

  const handleCloseAd = () => {
    resetAd()
    resetNextReward()
    setShowAd(false)
    setEpCurrentlyWatching(null)
  }

  const handleWatchedAd = () => {
    setIsCreatorReceivingFlixx(true)

    postWatchedAd({ id: ad.id })
      .then(handleBuyContent)

    handlePlayContent()
    resetAd()
    resetNextReward()
    setShowAd(false)
  }

  const handleNextEpisode = () => {
    setWeblink('')

    if (isPurchased(nextEpisode.uuid)) {
      setIsCreatorReceivingFlixx(false)

      setEpCurrentlyWatching(nextEpisode)
      const nextOfNext = episodes.find(ep => ep.order === nextEpisode.order + 1)
      setNextEpisode(nextOfNext)

    } else {
      setEpCurrentlyWatching(null)
      setEpisodeSelected(null)
      getNextReward()
      getAd()
    }
  }

  const handleClick = () => {
    if (!isLoggedIn) {
      Modal.open()
      GAHandler('ui_watch_notloggedin');
      return
    }

    if (isPurchased(episodeToWatch.uuid)) {
      setIsCreatorReceivingFlixx(false)
      handlePlayContent()
      GAHandler('ui_watch_content');
      return
    }

    const canPurchaseIt = parseFloat(episodeToWatch.price) <= balance

    if (canPurchaseIt) {
      handleBuyContent()
      GAHandler('ui_watch_buy');

    } else {
      getNextReward()
      getAd()
      GAHandler('ui_watch_ad');
    }
  }


  const getSeasonWithNextEpisode = () => {
    let season = serie.season[0];
    let episode = season.content[0];

    for (const idx in serie.season) {
      const nextSeason = serie.season[idx]
      const nextEpisode = getNextEpisode(nextSeason)

      if (nextEpisode) {
        season = nextSeason;
        episode = nextEpisode;
        break
      }
    }

    return { season, episode }
  }

  useEffect(() => {
    if (!playContent) return
    handleClick()
    setPlayContent(null)
  }, [playContent])

  useEffect(() => {
    if (ad && nextReward) setShowAd(true)
  }, [ad, nextReward])

  useEffect(() => {
    if (isLoggedIn && !isPurchasesLoaded) return
    if (!serie) return

    if (!customSeason) {
      const { season, episode } = getSeasonWithNextEpisode()

      setSeasonSelected(season)
      setNextEpisode(episode)
      if (episode.order !== 0) {
        setEpisodeSelected(episode)
      }
      return
    }

    const firstSeason = serie.season[0]
    const validSeasons = serie.season.map(s => s.number)
    const isValidCustomSeason = validSeasons.includes(parseInt(customSeason))
    const currentSeason = isValidCustomSeason
      ? serie.season.find(s => s.number === parseInt(customSeason))
      : firstSeason
    setSeasonSelected(currentSeason)

    const isValidCustomEp = customEpisode && parseInt(customEpisode) <= currentSeason.content.length
    if (isValidCustomSeason && isValidCustomEp) {
      const currentEp = currentSeason.content.find(ep => ep.order === customEpisode - 1)
      setEpisodeSelected(currentEp)

    } else {
      const nextEpisode = getNextEpisode(currentSeason)
      setNextEpisode(nextEpisode)
    }

  }, [serie, isPurchasesLoaded, isLoggedIn])


  useEffect(() => {
    if (isWaiting || !epCurrentlyWatching) return

    if (epCurrentlyWatching.hasDRM) {
      setWeblink('...')
    } else if (!epCurrentlyWatching.src) {
      apiFetch({ url: `/contents/${epCurrentlyWatching.uuid}/weblink` })
        .then(res => setWeblink(res.data))
    }
    // eslint-disable-next-line
  }, [epCurrentlyWatching, isWaiting])


  if (serieError?.code === 404) {
    return (
      <div style={{ height: '80vh', display: 'grid' }}>
        <ErrorCard
          description={t('contentNotFound')}
          code='W3001'
        />
      </div>
    )
  }


  if (!serie || !seasonSelected) {
    return <Loader />
  }


  if (showAd) {
    return (
      <PlayerLanding
        key='1'
        title={() => (
          <>
            {t('ad_title')}
            {' '}
            <Flixx
              price={nextReward}
              size='xl'
            />
          </>
        )}
        description={t('ad_description')}
        video={{ src: ad.url }}
        onEnd={handleWatchedAd}
        onClose={handleCloseAd}
        config={{
          afkTimeoutClose: false,
          nextEpisode: false,
          mode: 'ad',
          events: {
            onPlay: 'ad_presented',
            onEnd: 'ad_finished',
            onUnfinished: 'ad_unfinished',
          }
        }}

      />
    )
  }

  if (epCurrentlyWatching) {
    return (
      <PlayerLanding
        key='2'
        title={titlePlayer}
        description={descriptionPlayer}
        video={{ ...epCurrentlyWatching, src: weblink }}
        loaderEl={isCreatorReceivingFlixx
          ? () => <NewLoader text={t('creatorReceivingFlixx')} />
          : () => <NewLoader text={t('reproductionWillStartSoon')} />
        }
        onClose={handleCloseEpisode}
        nextEpisode={nextEpisode}
        onNextEpisode={handleNextEpisode}
        config={{
          events: {
            onPlay: 'player_playing',
            onEnd: 'player_ended',
            onUnfinished: 'ui_player_unfinished',
          }
        }
        }
      />
    )
  }

  const authorData = {
    name: author?.displayName || serie.author.displayName,
    avatar: author?.avatar || serie.author.Profile.avatar,
    videos: author?.numContents
  }

  const cover = {
    src: getThumbnail(selected),
    alt: selected.title
  }

  const episodesText = episodes.length === 1
    ? ` (1 ${t('episode')})`
    : ` (${episodes.length} ${t('episodes')})`


  const valueText = isPurchased(episodeToWatch?.uuid)
    ? ''
    : !episodeSelected && episodeToWatch.order === 0
      ? seasonNumber === 1 ? t('anteroom:startSeriesFor') : t('anteroom:startSeasonFor', { seasonNumber })
      : t('common:value')


  return (
    <Anteroom.Root>

      <Seo
        title={serie.title}
        description={serie.body}
      />

      <Anteroom.Info
        onClickCTA={handleClick}
        author={authorData}
        cover={cover}
        urlShare={`${environment.webUrl}/series/${serie.uuid}`}
        renderValueText={() => {
          if (!valueText || !episodeToWatch || !isLoggedIn) return null

          return (
            <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {valueText}
              <Flixx size='sm' price={episodeToWatch.price} />
            </span>
            </>
          )
        }}
      >
        {episodeSelected ? (
          <EpisodeInfo
            serie={serie.title}
            season={seasonSelected.number}
            episode={episodeSelected.order + 1}
            title={episodeSelected.title}
            duration={formatDuration(episodeSelected.duration)}
            body={episodeSelected.body}
            audios={episodeSelected.audioLang}
            subtitles={episodeSelected.subtitle}
          />
        ) : (
          <SerieInfo
            title={serie.title}
            body={serie.body}
            tag={serie.category.name}
            audioLang={serie.audioLang}
            seasons={serie.season}
            episodes={episodes}
          />
        )}
      </Anteroom.Info>

      <Anteroom.Carousel.Title>
        {serie.season.length > 1 ? (
          <Input.Select
            onChange={handleSelectSeason}
            selected={{
              label: `${t('season')} ${seasonSelected.number} (${seasonSelected.content.length} ${t('episodes')})`,
              value: seasonSelected.number
            }}
            options={serie.season.map(s => (
              {
                label: `${t('season')} ${s.number} (${s.content.length} ${t('episodes')})`,
                value: s.number
              }
            ))}
          />
        ) : (
          <Season>
            {`${t('season')} ${seasonSelected.number}`}
            <Episodes>{episodesText}</Episodes>
          </Season>
        )}
      </Anteroom.Carousel.Title>

      <Anteroom.Carousel.Slides defaultSlidePosition={episodeToWatch.order}>
        {episodes.map(ep => (
          <EpisodeCard
            key={ep.uuid}
            onClick={() => handleSelectEpisode(ep)}
            onClickCTA={() => {handleSelectEpisode(ep, true)}}
            title={`${ep.order + 1}. ${ep.title}`}
            duration={`${ep.duration} ${t('min')}`}
            body={ep.body}
            cover={getThumbnail(ep)}
            price={formatAmount(ep.price)}
            isSelected={episodeSelected?.order === ep.order}
            isPurchased={isPurchased(ep.uuid)}
          />
        ))}
      </Anteroom.Carousel.Slides>
    </Anteroom.Root>
  )
}

export default AnteroomSerie